import { useEffect, useState, useRef } from "react";
import useAuthStore from "../store/useAuthStore";
import Modal from "../component/Modal";
import { getTemplate } from "../services/TemplateServices";
import { sendEmail } from "../services/UserServices";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { deleteCard, getCards } from "../services/CardService";
import { ReactComponent as DeleteIcon } from "../assets/delete.svg";
import InfiniteScroll from "react-infinite-scroll-component";

const CardsPage = () => {
  const { role, token } = useAuthStore();
  const clearToken = useAuthStore((state) => state.clearToken);

  const [cards, setCards] = useState([]);
  const [limit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [selectedCard, setSelectedCard] = useState(null);
  const modalRef = useRef(null);

  const [templateModal, setTemplateModal] = useState(false);
  const [templateList, setTemplateList] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedUser, setSelectedUser] = useState("");

  const [userImageModal, setUserImageModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");


  const navigate = useNavigate();

  useEffect(() => {
    if (!token) navigate("/");
  }, [token, navigate]);

  // Fetch cards
  const fetchCards = async () => {
    try {
      const response = await getCards(limit, offset, debouncedSearch);

      if (response.status !== 200) throw new Error("Fetch error");

      const { cards: newCards, count } = response.data;

      setCards((prev) => [...prev, ...newCards]);
      setOffset((prev) => prev + limit);
      setHasMore(cards.length + newCards.length < count);
    } catch (error) {
      console.error("Error fetching cards:", error);
    }
  };

useEffect(() => {
  const handler = setTimeout(() => {
    setDebouncedSearch(searchTerm);
  }, 500);

  return () => clearTimeout(handler);
}, [searchTerm]);

// Fetch cards when debounced search changes
useEffect(() => {
  setCards([]);
  setOffset(0);
  setHasMore(true);
  fetchCards();
  // eslint-disable-next-line
}, [debouncedSearch]);


  // Modal close handler
  const handleClickOutsideModal = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      setSelectedCard(null);
    }
  };

  useEffect(() => {
    if (selectedCard) {
      document.addEventListener("mousedown", handleClickOutsideModal);
    }
    return () => document.removeEventListener("mousedown", handleClickOutsideModal);
  }, [selectedCard]);

  const sendEmailBtn = (email) => {
    setTemplateModal(true);
    setSelectedUser(email);
  };

  const allTemplates = async () => {
    const res = await getTemplate();
    setTemplateList(res?.data?.data || []);
  };

  useEffect(() => {
    allTemplates();
  }, []);

  const handleSendEmail = async () => {
    try {
      const response = await sendEmail(selectedTemplateId, selectedUser);
      toast.success(response?.data?.message || "Email sent");
    } catch {
      toast.error("Something went wrong");
    }
  };

  const handleLogout = () => clearToken();

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this card?")) return;

    try {
      const res = await deleteCard(id);
      if (res.status === 200) {
        toast.success(res?.data?.message);
        setCards([]);
        setOffset(0);
        setHasMore(true);
        fetchCards();
      }
    } catch {
      toast.error("Something went wrong");
    }
  };

  return (
    <div className="p-6">

      <div className="flex justify-between">
        <h2 className="text-2xl font-bold mb-4">Visiting Cards</h2>
        <button
          className="bg-blue-700 text-white rounded-md px-4 h-8"
          onClick={handleLogout}
        >
          Logout
        </button>
      </div>

      <div className="flex justify-center gap-3 mb-4">

        <input
          type="text"
          placeholder="Search name, email, company..."
          value={searchTerm}
          onChange={(e) => {
            setCards([]);
            setOffset(0);
            setHasMore(true);
            setSearchTerm(e.target.value);
          }}
          className="border px-3 py-1 rounded-md w-64"
        />
      </div>

      {/* Infinite Scroll */}
      <div id="scrollableDiv" style={{ height: "80vh", overflowY: "auto" }}>
        <InfiniteScroll
          dataLength={cards.length}
          next={fetchCards}
          hasMore={hasMore}
          loader={<h4 className="text-center py-4">Loading...</h4>}
          endMessage={<p className="text-center py-4 font-semibold">— No more cards —</p>}
          scrollableTarget="scrollableDiv"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((card) => (
              <div
                key={card.id}
                className="bg-white shadow-md p-4 rounded-lg hover:shadow-lg transition cursor-pointer"
                onClick={() => setSelectedCard(card)}
              >
                <div className="flex justify-between">
                  <h3 className="text-lg font-semibold">
                    {card.firstName} {card.lastName}
                  </h3>

                  {role === "admin" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(card.id);
                      }}
                    >
                      <DeleteIcon className="h-4 w-6" />
                    </button>
                  )}
                </div>

                <p className="text-gray-600">{card.position}</p>
                <p className="text-gray-500">{card.company}</p>
                <p className="text-blue-500">{card.email}</p>
                <p className="text-gray-500">{card.mobile || "-"}</p>

                <a
                  href={card.website}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {card.website}
                </a>
              </div>
            ))}
          </div>
        </InfiniteScroll>
      </div>

      {/* Card Modal */}
      {selectedCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div
            ref={modalRef}
            className="bg-white p-6 rounded-lg shadow-lg max-w-lg relative"
          >
            <button
              onClick={() => setSelectedCard(null)}
              className="absolute top-2 right-2"
            >
              &times;
            </button>

            <img
              src={selectedCard.imageUrl}
              alt=""
              className="w-full max-h-96 object-cover rounded-md mb-3"
            />

            <h3 className="text-lg font-semibold">
              {selectedCard.firstName} {selectedCard.lastName}
            </h3>
            <p>{selectedCard.position}</p>
            <p>{selectedCard.company}</p>
            <p>{selectedCard.email}</p>
            <p>{selectedCard.mobile}</p>

            {selectedCard.remark && (
              <p className="mt-2">
                <b>Remark:</b> {selectedCard.remark}
              </p>
            )}

            <p className="mt-2">
              <b>Back Image:</b>{" "}
              {selectedCard.backImageUrl ? (
                <a
                  href={selectedCard.backImageUrl}
                  target="_blank"
                  className="text-blue-600 underline"
                  rel="noreferrer"
                >
                  View
                </a>
              ) : (
                "No back image"
              )}
            </p>

            {selectedCard.userImage && (
              <p
                className="text-blue-700 underline cursor-pointer mt-2"
                onClick={() => {
                  setUserImageModal(true);
                  setPreviewImage(selectedCard.userImage);
                }}
              >
                Preview user image
              </p>
            )}

            {role === "admin" && (
              <div className="mt-4 text-center">
                <button
                  className="bg-blue-500 text-white px-4 py-2 rounded-md"
                  onClick={() => sendEmailBtn(selectedCard.email)}
                >
                  Send Email
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Template Modal */}
      <Modal isOpen={templateModal} onClose={() => setTemplateModal(false)}>
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4">Select Template</h2>

          <select
            className="w-full border p-2 rounded-md"
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
          >
            <option value="">Choose a template</option>
            {templateList.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>

          <div className="mt-6 flex justify-end gap-3">
            <button className="px-4 py-2 border rounded-md" onClick={() => setTemplateModal(false)}>
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-blue-500 text-white rounded-md"
              disabled={!selectedTemplateId}
              onClick={() => {
                handleSendEmail();
                setTemplateModal(false);
              }}
            >
              Send
            </button>
          </div>
        </div>
      </Modal>

      {/* User Image Modal */}
      <Modal
        isOpen={userImageModal}
        onClose={() => {
          setUserImageModal(false);
          setPreviewImage(null);
        }}
      >
        <div className="flex justify-center items-center h-72">
          <img
            src={previewImage}
            alt="user"
            className="max-h-full max-w-full object-contain rounded-md"
          />
        </div>
      </Modal>
    </div>
  );
};

export default CardsPage;
